import { pluralize } from '../../../utils/text-utils.ts'
import { SeverityBadge } from '../../badges/severity-badge.tsx'
import { DETECTION_LENSES } from '../beacon-run.ts'
import * as styles from './beacon-coverage.css.ts'
import { BEACON_DISMISSED_LEADS_ID, DISMISSED_BY_LABELS, dismissedLeadAnchor } from './beacon-dismissed-leads.tsx'
import {
  checkLabel,
  formatTokens,
  KIND_LABELS,
  STATUS_LABELS,
  type CoverageEntity,
  type CoverageModel,
} from './coverage-model.ts'
import { useVerifiedFindingLookup, verifiedFindingRows } from './verified-findings.ts'

const verifiedCount = (count: number) => `${count} verified ${pluralize(count, 'finding', 'findings')}`

const dismissedCount = (count: number) => `${count} ${pluralize(count, 'lead', 'leads')} dismissed`

/** One line about a part of the attack surface, for the inspector under the picture. */
export const describeEntity = (entity: CoverageEntity): React.ReactNode => {
  const findings = entity.findings.length
  const effort = `${formatTokens(entity.tokens)} tokens`
  const checks = `${entity.lenses.length} ${pluralize(entity.lenses.length, 'check', 'checks')} run`
  return (
    <>
      <b>{entity.name}</b> — {STATUS_LABELS[entity.status]}, {checks}, {entity.isApproximate ? '≈' : ''}
      {effort}
      {findings > 0 && `, ${verifiedCount(findings)}`}
      {entity.dismissed.length > 0 && `, ${dismissedCount(entity.dismissed.length)}`}
      {entity.reason && ` · ${entity.reason}`}
    </>
  )
}

const chipState = (model: CoverageModel, entity: CoverageEntity, lens: string) =>
  lens === 'ai_scan_sink_backward' && model.isSinkLensUnrun ? 'unrun' : entity.lenses.includes(lens) ? 'on' : 'off'

/**
 * Everything the summary knows about one part: its status and why, which checks ran on it, the effort
 * spent, and the verified findings on it, each opening like any other finding of the scan.
 */
export const BeaconCoverageDetail: React.FC<{ model: CoverageModel; entityKey?: string }> = ({ model, entityKey }) => {
  const lookup = useVerifiedFindingLookup()
  const entity = entityKey ? model.entities.get(entityKey) : undefined
  if (!entity) {
    return (
      <>
        <p className={styles.subHeading}>Details</p>
        <p className={styles.detailText}>
          Click a tile to see which checks ran on it, how much effort went in, and what was found.
        </p>
      </>
    )
  }
  const operation = model.operations.get(entity.key)
  const rows = verifiedFindingRows({ findingIds: entity.findings, lookup })
  const verifyTokens = entity.tokensByLens.ai_scan_verify ?? 0
  return (
    <>
      <p className={styles.subHeading}>{KIND_LABELS[entity.kind]}</p>
      <h3 className={styles.detailTitle}>{entity.name}</h3>
      <span className={styles.status({ status: entity.status })}>{STATUS_LABELS[entity.status]}</span>
      {entity.reason && <p className={styles.detailText}>{entity.reason}</p>}
      <p className={styles.subHeading}>Checks run</p>
      <div className={styles.lensChips} role="group" aria-label="Checks run">
        {DETECTION_LENSES.map(lens => (
          <span key={lens} className={styles.lensChip({ state: chipState(model, entity, lens) })}>
            {checkLabel(lens)}
          </span>
        ))}
      </div>
      <dl className={styles.keyValues}>
        {operation && (
          <>
            <dt>Location</dt>
            <dd className={styles.findingLocation}>{operation.location}</dd>
            <dt>Caller · effect</dt>
            <dd>
              {operation.caller} · {operation.effect}
            </dd>
          </>
        )}
        <dt>Effort</dt>
        <dd>
          {entity.isApproximate ? '≈' : ''}
          {formatTokens(entity.tokens)} tokens
          {verifyTokens > 0 && ` (${formatTokens(verifyTokens)} verifying)`}
        </dd>
        <dt>File reads</dt>
        <dd>
          {entity.isApproximate ? '≈' : ''}
          {Math.round(entity.files)}
        </dd>
      </dl>
      <p className={styles.subHeading}>
        {entity.findings.length > 0 ? verifiedCount(entity.findings.length) : 'No verified findings'}
      </p>
      {rows.length > 0 && (
        <ol className={styles.findingList} aria-label="Verified findings">
          {rows.map(row => (
            <li key={row.id} className={styles.findingItem}>
              <span className={styles.findingRow}>
                {row.severity && <SeverityBadge severity={row.severity} />}
                <button type="button" className={styles.findingLink} onClick={row.onOpen}>
                  {row.title}
                </button>
              </span>
            </li>
          ))}
        </ol>
      )}
      {entity.findings.length === 0 && (
        <p className={styles.detailText}>
          {entity.status === 'examined'
            ? 'Examined; no weakness here was verified.'
            : 'Not examined, so no claim is made about this part.'}
        </p>
      )}
      {entity.dismissed.length > 0 && (
        <>
          <p className={styles.subHeading}>{dismissedCount(entity.dismissed.length)} here</p>
          <ol className={styles.findingList} aria-label="Dismissed leads">
            {entity.dismissed.map(lead => (
              <li key={lead.finding_uuid} className={styles.dismissedItem}>
                <a href={`#${dismissedLeadAnchor(lead)}`} className={styles.findingLink}>
                  {lead.title}
                </a>
                <span className={styles.findingLocation}>{DISMISSED_BY_LABELS[lead.dismissed_by]}</span>
              </li>
            ))}
          </ol>
          <a href={`#${BEACON_DISMISSED_LEADS_ID}`} className={styles.inlineLink}>
            Every lead Beacon dismissed
          </a>
        </>
      )}
    </>
  )
}
